import {ChangeDetectionStrategy, Component, Input, OnDestroy, OnInit} from '@angular/core';
import { Router } from '@angular/router';
import {ReplaySubject, Subscription} from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { AppService } from '@irb/app.service';
import { PageService } from '@irb-common/services/page.service';
import { IrbDashboardService } from '@irb/irb-dashboard/irb-dashboard.service';
import {IGlobalSettings, ITopModelDetails} from '@irb/irb-dashboard/irb-dashboard.model';
import { DataTransferService } from '@irb/irb-common/services/data-transfer.service';
import { IApplyFilterOnLanding } from '@irb/app.model';
import { AnalyticsService } from '@irb/irb-common/analytics/analytics.service';

@Component({
  standalone: false,
  selector: 'irb-module-models',
  templateUrl: './module-models.component.html',
  styleUrls: ['./module-models.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager
})
export class ModuleModelsComponent implements OnInit, OnDestroy {
  private destroyed$: ReplaySubject<boolean> = new ReplaySubject(1);
  private _data: ITopModelDetails;
  chartData: any;
  percentages: Array<number> = [];

  colors: Array<string> = [
    "#0092CC",
    "#BE650E",
    "#5A6772",
    "#BF739D",
    "#6D8B41",
    "#3A5D92"
  ];

  options: any = {
    responsive: true,
    maintainAspectRatio: false,
    rotation: Math.PI,
    cutout: '75%',
    plugins: {
      legend: {
        display: false
      },
      tooltip: {
        callbacks: {
          label: (context: any) => {
            const label = this.truncate(context.label, 15);
            const value = Math.floor(((context.dataset.data[context.dataIndex] / 100) * 100) + 0.5);
            return label + " (" + value + "%)";
          }
        }
      }
    }
  };
  private masterAccountSubscription: Subscription;
  selectedMasterAccounts: Array<string> = [];
  initailLoadPassed = false;
  @Input() customizableMode: boolean = false;
  @Input() singleWidth: boolean;
  @Input() globalSettings: IGlobalSettings;
  percentagesSkeleton: number[] = [10, 35, 25, 15, 10, 5];
  aumRandom: number[] = [70, 25, 55, 45, 15, 10];
  modelNameRandom: number[] = [50, 85, 95, 45, 65, 80];
  portfoliosRandom: number[] = [20, 25, 15, 30, 15, 10];
  customizeColors: string[] =  ['#E9E9E9', '#E9E9E9', '#E9E9E9', '#E9E9E9', '#E9E9E9', '#E9E9E9'];

  constructor(
    public as: AppService,
    public ps: PageService,
    private dbs: IrbDashboardService,
    private router: Router,
    public dataTransferService: DataTransferService,
    private ats: AnalyticsService
  ) { }

  ngOnInit() {
    if (this.customizableMode)
      return;

    this.ps.getUserDetails().pipe(takeUntil(this.destroyed$)).subscribe((d) => {
      if (d) {
        this.ps.deserializePageState(this.as.getFromSession("pageState"), ["modelGridDiv"]);

        this.masterAccountSubscription = this.dbs.currentMasterAccountData.subscribe(passedSelectedMasterAccounts => {
          if (this.initailLoadPassed) {
            this.data = null;
            this.selectedMasterAccounts = passedSelectedMasterAccounts;
            if (this.selectedMasterAccounts && this.selectedMasterAccounts.length > 0)
              this.getTopModelsForSelectedAccounts();
            else
              this.getTopModelsForAllAccounts();
          }
        });

        this.initailLoadPassed = true;
        this.selectedMasterAccounts = this.dbs.selectedMasterAccounts;
        if (this.selectedMasterAccounts && this.selectedMasterAccounts.length > 0)
          this.getTopModelsForSelectedAccounts();
        else
          this.getTopModelsForAllAccounts();

      }
    });

  }

  getTopModelsForAllAccounts() {
    this.dbs.getTopModels().subscribe((d) => {
      this.data = d as ITopModelDetails;
      this.update();
    });
  }

  getTopModelsForSelectedAccounts() {
    this.dbs.getTopModelsForMasters(this.selectedMasterAccounts).subscribe({
      next: (d: any) => {
        this.data = d as ITopModelDetails;
        this.update();
      }
    });
  }

  ngOnDestroy() {
    this.destroyed$.next(true);
    this.destroyed$.complete();
    if (this.masterAccountSubscription)
      this.masterAccountSubscription.unsubscribe();
  }

  update() {
    const labels: Array<string> = [];
    const percentages: Array<number> = [];
    const t = this;

    if (!this.data)
      this.data = {topModels:[], remainingModels: []};

    this.data.topModels.sort(function (a: any, b: any) {
      return b.aum - a.aum;
    });

    let totalAUM = 0;

    this.data.remainingModels.forEach((item: any) => {
      totalAUM += item.aum;
    });

    this.data.topModels.forEach((item: any) => {
      totalAUM += item.aum;
    });

    this.data.topModels.forEach((item: any) => {
      labels.push(item.modelName);
      if (totalAUM)
        percentages.push((item.aum / totalAUM) * 100);
    });

    this.data.remainingModels.forEach((item: any) => {
      labels.push("Other");
      if (totalAUM)
        percentages.push((item.aum / totalAUM) * 100);
    });

    this.percentages = percentages;

    this.chartData = {
      labels: labels,
      datasets: [
        {
          data: (totalAUM !== 0) ? percentages : [100],
          backgroundColor: (totalAUM !== 0) ? this.colors : ["#E9E9E9"],
          hoverBackgroundColor: (totalAUM !== 0) ? this.colors : ["#E9E9E9"],
          borderWidth: 2,
          hoverBorderColor: ["#FFFFFF"]
        }
      ]
    };

    this.options.plugins.tooltip.enabled = (totalAUM > 0);
  }

  truncate(s: string, n: number) {
    return (s.length > n) ? s.substring(0, n - 1) + '...' : s;
  }

  showModels() {
    this.ats.trackPassiveEvent("lc_topmodels");
    this.router.navigate(['/models/models']);
  }

  showModelDetail(modelId: string, modelName: string) {
    this.ats.trackPassiveEvent("lc_modelname");

    const state: IApplyFilterOnLanding = {
      isUpdatRequired: true,
      openEditModel: true,
      modelId: modelId,
      filterModel: { name: 'name', value: modelName, isUpdatRequired: true }
    } as IApplyFilterOnLanding;

    this.router.navigateByUrl('/models/models', { state: state });
  }

  get data() {
    return this._data;
  }

  set data(d: ITopModelDetails) {
    this._data = d;
  }

}
